/**
 * Data Ops · Review Queue — Feb 2026
 * ------------------------------------
 * Human-in-the-loop review for asset field values proposed by backfill
 * agents (RoA, TA, indication, modality, …) from unverified web sources
 * or that contradict an internal-profile value. See
 * `services/provenance.py` for the guardrail model.
 *
 * A reviewer sees each proposal with:
 *   • Asset / company context
 *   • Which field is being changed and to what
 *   • The source URL + verbatim quote the agent found
 *   • Whether it contradicts an existing internal-profile value
 * and one-click Accept / Reject buttons.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Check, X, Loader2, ExternalLink, ShieldCheck,
  AlertTriangle, HelpCircle, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api/data-provenance';

const TIER_META = {
  'internal':        { label: 'Internal profile',        icon: ShieldCheck,   cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  'trusted-web':     { label: 'Trusted web',             icon: ShieldCheck,   cls: 'text-blue-700 bg-blue-50 border-blue-200' },
  'unverified-web':  { label: 'Unverified web',          icon: AlertTriangle, cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  'rejected':        { label: 'Rejected',                icon: X,             cls: 'text-rose-700 bg-rose-50 border-rose-200' },
};

function TierPill({ tier }) {
  const meta = TIER_META[tier] || { label: tier, icon: HelpCircle, cls: 'text-slate-600 bg-slate-50 border-slate-200' };
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded border ${meta.cls}`}
      data-testid={`prov-tier-${tier}`}
    >
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

export default function ReviewQueuePage() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [reverifying, setReverifying] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState('');

  const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectWhere = (pred) => setSelected(new Set(rows.filter(pred).map(r => r.id)));
  const trustedSafe = (r) => r.tier === 'trusted-web' && !r.existing_from_internal;

  const bulk = async (mode) => {
    const ids = [...selected];
    if (!ids.length) return;
    let reason = '';
    if (mode === 'reject') {
      reason = window.prompt(`Reject ${ids.length} proposal${ids.length === 1 ? '' : 's'} — optional reason:`, '');
      if (reason === null) return;
    } else if (!window.confirm(`Accept ${ids.length} proposal${ids.length === 1 ? '' : 's'} and apply the proposed values to the assets?`)) return;
    setBulkBusy(mode);
    try {
      const r = await axios.post(`${API}/bulk-${mode}`, { ids, reason }, { withCredentials: true, timeout: 180000 });
      const n = mode === 'accept' ? r.data.applied : r.data.rejected;
      const failedIds = new Set((r.data.failed || []).map(f => f.id));
      setRows(rs => rs.filter(x => !ids.includes(x.id) || failedIds.has(x.id)));
      setSelected(new Set());
      if (failedIds.size) toast.warning(`${n} ${mode === 'accept' ? 'applied' : 'rejected'} · ${failedIds.size} failed (left in the queue)`);
      else toast.success(`${n} proposal${n === 1 ? '' : 's'} ${mode === 'accept' ? 'applied — assets updated' : 'rejected'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Bulk ${mode} failed`);
    } finally {
      setBulkBusy('');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pending`, { withCredentials: true });
      setRows(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load review queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doAccept = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/${id}/accept`, {}, { withCredentials: true });
      toast.success('Applied — asset updated');
      setRows(r => r.filter(x => x.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Accept failed');
    } finally {
      setBusyId('');
    }
  };

  const doReject = async (id) => {
    const reason = window.prompt('Optional — why are you rejecting this proposal?', '');
    if (reason === null) return; // cancelled
    setBusyId(id);
    try {
      await axios.post(`${API}/${id}/reject`, { reason }, { withCredentials: true });
      toast.success('Rejected — value not applied');
      setRows(r => r.filter(x => x.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setBusyId('');
    }
  };

  const runReVerify = async () => {
    if (!window.confirm(
      'Re-verify all existing asset fields?\n\n' +
      'This walks every asset and stamps existing field values as "internal" ' +
      'provenance so the contradiction shield protects them from future silent ' +
      'overwrites. Idempotent — safe to run more than once.'
    )) return;
    setReverifying(true);
    try {
      const r = await axios.post(`${API}/re-verify`, {}, { withCredentials: true });
      toast.success(`Scanned ${r.data.scanned} assets · stamped ${r.data.legacy_provenance_rows_created} new legacy rows`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-verify failed');
    } finally {
      setReverifying(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="data-provenance-review-queue">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Link
          to="/data-ops/ta-backfill"
          className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-[12px]"
          data-testid="review-queue-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> Back to Data Ops
        </Link>
      </div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-[22px] font-semibold text-slate-900" data-testid="review-queue-title">
            Data Review Queue
          </h1>
          <p className="text-[13px] text-slate-600 mt-1 max-w-2xl">
            Backfill agents can enrich assets from the web — but every proposed
            value flows through the Source-Control Framework first. Only values
            from trusted, cited sources auto-apply. Anything else lands here for
            you to accept or reject in one click.
          </p>
        </div>
        <Button
          onClick={runReVerify}
          disabled={reverifying}
          variant="outline"
          className="h-8 text-[12px] shrink-0"
          data-testid="review-queue-reverify-btn"
        >
          {reverifying
            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Running…</>
            : <><RefreshCw className="w-3 h-3 mr-1" />Re-verify existing assets</>}
        </Button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-3 mb-5" data-testid="review-queue-summary">
        <SummaryCard label="Pending review"           value={rows.length}                                    tone="amber" />
        <SummaryCard label="Contradicts internal"     value={counts.contradicts_internal || 0}               tone="rose"  />
        <SummaryCard label="Trusted-web conflict"     value={counts.trusted_conflict || 0}                   tone="blue"  />
        <SummaryCard label="Unverified web"           value={counts.unverified || 0}                         tone="amber" />
      </div>

      {/* Body */}
      {loading ? (
        <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading proposals…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="p-10 text-center bg-emerald-50 border-2 border-emerald-200 rounded-lg"
          data-testid="review-queue-empty"
        >
          <ShieldCheck className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <div className="text-[15px] font-semibold text-emerald-900">All clear</div>
          <p className="text-[13px] text-emerald-800 mt-1">
            No pending proposals right now. The guardrails are silently blocking
            anything without a citation, and only trusted sources are auto-applying.
          </p>
        </div>
      ) : (
        <div className="border rounded-lg bg-white divide-y" data-testid="review-queue-list">
          <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 px-4 py-2 bg-slate-50/95 backdrop-blur border-b" data-testid="review-queue-bulk-bar">
            <label className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer">
              <input type="checkbox" className="h-3.5 w-3.5 accent-emerald-600"
                checked={rows.length > 0 && selected.size === rows.length}
                onChange={(e) => e.target.checked ? selectWhere(() => true) : setSelected(new Set())}
                data-testid="bulk-select-all" />
              Select all ({rows.length})
            </label>
            <Button variant="outline" className="h-7 text-[11px]" onClick={() => selectWhere(trustedSafe)} data-testid="bulk-select-trusted">
              Select trusted-web only ({rows.filter(trustedSafe).length})
            </Button>
            <span className="text-[11px] text-slate-500 ml-1" data-testid="bulk-selected-count">{selected.size} selected</span>
            <div className="ml-auto flex items-center gap-2">
              <Button className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!selected.size || !!bulkBusy} onClick={() => bulk('accept')} data-testid="bulk-accept-btn">
                {bulkBusy === 'accept' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />} Accept selected{selected.size ? ` (${selected.size})` : ''}
              </Button>
              <Button variant="outline" className="h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50" disabled={!selected.size || !!bulkBusy} onClick={() => bulk('reject')} data-testid="bulk-reject-btn">
                {bulkBusy === 'reject' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <X className="w-3 h-3 mr-1" />} Reject selected
              </Button>
            </div>
          </div>
          {rows.map(row => (
            <ReviewRow
              key={row.id}
              row={row}
              busy={busyId === row.id || !!bulkBusy}
              checked={selected.has(row.id)}
              onToggle={() => toggleSel(row.id)}
              onAccept={() => doAccept(row.id)}
              onReject={() => doReject(row.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function SummaryCard({ label, value, tone }) {
  const toneMap = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose:  'bg-rose-50 border-rose-200 text-rose-900',
    blue:  'bg-blue-50 border-blue-200 text-blue-900',
  };
  return (
    <div className={`p-3 rounded-lg border-2 ${toneMap[tone]}`}>
      <div className="text-[11px] font-medium opacity-80">{label}</div>
      <div className="text-[24px] font-semibold tabular-nums mt-1">{value}</div>
    </div>
  );
}


function ReviewRow({ row, busy, checked, onToggle, onAccept, onReject }) {
  return (
    <div className={`p-4 hover:bg-slate-50/50 ${checked ? 'bg-emerald-50/40' : ''}`} data-testid={`review-row-${row.id}`}>
      <div className="flex items-start justify-between gap-4">
        <input type="checkbox" className="mt-1 h-3.5 w-3.5 accent-emerald-600 shrink-0" checked={!!checked} onChange={onToggle} data-testid={`review-row-select-${row.id}`} />
        {/* Left column — context */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              {row.field}
            </span>
            <TierPill tier={row.tier} />
            {row.existing_from_internal && (
              <span className="text-[10.5px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                CONTRADICTS INTERNAL
              </span>
            )}
          </div>
          <div className="text-[14px] text-slate-900">
            <span className="font-medium">{row.asset_name || row.asset_id}</span>
            {row.company_name && (
              <span className="text-slate-500"> · {row.company_name}</span>
            )}
          </div>

          {/* Value diff */}
          <div className="mt-2 grid grid-cols-2 gap-3 text-[12.5px]">
            <div className="p-2 rounded bg-slate-50 border">
              <div className="text-[10.5px] font-semibold text-slate-500 uppercase mb-0.5">Current</div>
              <div className="text-slate-700 break-words">
                {row.existing_value || <em className="text-slate-400">empty</em>}
              </div>
            </div>
            <div className="p-2 rounded bg-emerald-50 border border-emerald-200">
              <div className="text-[10.5px] font-semibold text-emerald-700 uppercase mb-0.5">Proposed</div>
              <div className="text-emerald-900 font-medium break-words">{row.value}</div>
            </div>
          </div>

          {/* Source */}
          <div className="mt-2 text-[12px] text-slate-600">
            <span className="font-semibold text-slate-700">Source:</span>{' '}
            {row.source_url ? (
              <a
                href={row.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
              >
                {shortHost(row.source_url)} <ExternalLink className="w-2.5 h-2.5" />
              </a>
            ) : <span className="text-slate-400">no URL</span>}
            <span className="text-slate-400"> · agent: </span>
            <span className="font-mono text-[11px]">{row.extracted_by}</span>
            {row.confidence && (
              <><span className="text-slate-400"> · confidence: </span>
              <span>{row.confidence}</span></>
            )}
          </div>
          {row.evidence_snippet && (
            <blockquote className="mt-2 text-[12px] text-slate-700 italic border-l-2 border-slate-300 pl-2">
              "{row.evidence_snippet}"
            </blockquote>
          )}
          {row.reason && (
            <div className="mt-1 text-[11px] text-slate-500">{row.reason}</div>
          )}
        </div>

        {/* Right column — actions */}
        <div className="shrink-0 flex flex-col gap-2 w-32">
          <Button
            size="sm"
            onClick={onAccept}
            disabled={busy}
            className="h-8 text-[12px] bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`accept-${row.id}`}
          >
            {busy
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <><Check className="w-3 h-3 mr-1" /> Accept</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            disabled={busy}
            className="h-8 text-[12px] text-rose-700 border-rose-200 hover:bg-rose-50"
            data-testid={`reject-${row.id}`}
          >
            <X className="w-3 h-3 mr-1" /> Reject
          </Button>
        </div>
      </div>
    </div>
  );
}


function shortHost(url) {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
